import { parseAwsJson } from '@/utils/graphql'

export const DEFAULT_RESTRICTED_CALLER_AUDIO_TEXT =
  'You are not a verified user for our agent to take your call. Thanks for calling anyway.'

function pickString(source, ...keys) {
  if (!source || typeof source !== 'object') return ''
  for (const key of keys) {
    const value = source[key]
    if (value != null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

/** Parse org.chatbot_details (object or JSON string). */
export function parseChatbotDetails(raw) {
  const parsed = parseAwsJson(raw) ?? raw
  if (!parsed || typeof parsed !== 'object') {
    return {}
  }
  return parsed
}

export function readOrgAiEngine(org) {
  if (!org || typeof org !== 'object') return ''
  const direct = pickString(
    org,
    'ai_engine',
    'aiEngine',
    'org_ai_engine',
    'orgAiEngine',
    'voice_ai_engine',
    'voiceAiEngine',
  )
  if (direct) return direct
  const details = parseChatbotDetails(org.chatbot_details)
  return pickString(details, 'ai_engine', 'aiEngine')
}

export function orgUsesGeminiAiEngine(org) {
  const engine = readOrgAiEngine(org).toUpperCase()
  return engine === 'GEMINI' || engine.includes('GEMINI')
}

export function orgUsesNovaSonicAiEngine(org) {
  const engine = readOrgAiEngine(org).toUpperCase()
  return engine === 'NOVA_SONIC' || engine.includes('NOVA_SONIC')
}

export function orgSupportsChatbotLanguage(org) {
  return orgUsesGeminiAiEngine(org) || orgUsesNovaSonicAiEngine(org)
}

/** BCP-47 locale → display label for agent language settings. */
export const AGENT_LANGUAGE_MAP = {
  'en-IN': 'English',
  'hi-IN': 'Hindi',
  'ml-IN': 'Malayalam',
  'ta-IN': 'Tamil',
  'kn-IN': 'Kannada',
  'te-IN': 'Telugu',
  'bn-IN': 'Bengali',
  'mr-IN': 'Marathi',
  'gu-IN': 'Gujarati',
  'pa-IN': 'Punjabi',
  'or-IN': 'Odia',
  'as-IN': 'Assamese',
  'ur-IN': 'Urdu',
  'si-LK': 'Sinhala',
  'ne-NP': 'Nepali',
}

const AGENT_LANGUAGE_CODES = Object.keys(AGENT_LANGUAGE_MAP)

function buildAgentLanguageOptions(codes) {
  return codes.map((value) => ({
    label: AGENT_LANGUAGE_MAP[value],
    value,
  }))
}

/** All supported locales for Gemini AI agents. */
export const GEMINI_AGENT_LANGUAGE_OPTIONS = buildAgentLanguageOptions(
  AGENT_LANGUAGE_CODES,
)

/** English and Hindi only for Nova Sonic agents. */
export const NOVA_SONIC_AGENT_LANGUAGE_OPTIONS = buildAgentLanguageOptions(
  AGENT_LANGUAGE_CODES.slice(0, 2),
)

export function getAgentLanguageOptionsForOrg(org) {
  if (orgUsesNovaSonicAiEngine(org)) {
    return NOVA_SONIC_AGENT_LANGUAGE_OPTIONS
  }
  if (orgUsesGeminiAiEngine(org)) {
    return GEMINI_AGENT_LANGUAGE_OPTIONS
  }
  return []
}

const LEGACY_LANGUAGE_CODE_MAP = {
  en: 'en-IN',
  hi: 'hi-IN',
  ml: 'ml-IN',
  ta: 'ta-IN',
  kn: 'kn-IN',
  te: 'te-IN',
  bn: 'bn-IN',
  mr: 'mr-IN',
  gu: 'gu-IN',
  pa: 'pa-IN',
  or: 'or-IN',
  as: 'as-IN',
  ur: 'ur-IN',
  si: 'si-LK',
  ne: 'ne-NP',
}

function canonicalizeLanguageCode(code) {
  const normalized = String(code ?? '').trim()
  if (!normalized) return ''

  const lower = normalized.toLowerCase()
  if (LEGACY_LANGUAGE_CODE_MAP[lower]) {
    return LEGACY_LANGUAGE_CODE_MAP[lower]
  }

  const canonical = AGENT_LANGUAGE_CODES.find(
    (locale) => locale.toLowerCase() === lower,
  )
  return canonical ?? normalized
}

export function normalizeAgentLanguageCode(value) {
  return canonicalizeLanguageCode(value)
}

/** @deprecated Use normalizeAgentLanguageCode */
export function normalizeGeminiLanguageCode(value) {
  return normalizeAgentLanguageCode(value)
}

export function readChatbotDetailsFromOrg(org) {
  const details = parseChatbotDetails(org?.chatbot_details)
  return {
    chatbot_name: pickString(
      details,
      'chatbot_name',
      'chatbotName',
    ),
    chatbot_gender: pickString(
      details,
      'chatbot_gender',
      'chatbotGender',
      'gender',
    ),
    chatbot_language: normalizeAgentLanguageCode(
      pickString(
        details,
        'chatbot_language',
        'chatbotLanguage',
        'language',
      ),
    ),
  }
}

export function readRestrictedCallerAudioTextFromOrg(org) {
  return pickString(
    org,
    'restricted_caller_audio_text',
    'restrictedCallerAudioText',
  )
}

export function readTextAudioUrlFromOrg(org) {
  return pickString(org, 'text_audio_url', 'textAudioUrl')
}

export function readGreetingMessageFromOrg(org) {
  return pickString(org, 'greeting_message', 'greetingMessage')
}

export function readSystemPromptFromOrg(org) {
  return pickString(org, 'system_prompt', 'systemPrompt')
}

export function buildChatbotDetailsInput(form, options = {}) {
  const { includeLanguage = true } = options
  const payload = {
    chatbot_name: String(form.chatbot_name ?? '').trim(),
    chatbot_gender: String(form.chatbot_gender ?? '').trim(),
  }
  if (includeLanguage) {
    payload.chatbot_language = String(form.chatbot_language ?? '').trim()
  }
  return payload
}

/** @deprecated Use readTextAudioUrlFromOrg */
export function readRestrictedCallerAudioUrlFromOrg(org) {
  return readTextAudioUrlFromOrg(org)
}

export const CHATBOT_GENDER_OPTIONS = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
]

/** @deprecated Use getAgentLanguageOptionsForOrg or GEMINI_AGENT_LANGUAGE_OPTIONS. */
export const CHATBOT_LANGUAGE_OPTIONS = GEMINI_AGENT_LANGUAGE_OPTIONS
